import React, { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

type Getters<T> = Record<string, (r: T) => any>;

export function useSort<T>(rows: T[], get: Getters<T>, initial: string, initialDir: 1 | -1 = 1) {
  const [key, setKey] = useState(initial);
  const [dir, setDir] = useState<1 | -1>(initialDir);
  const sorted = useMemo(() => {
    const g = get[key];
    if (!g) return rows;
    return [...rows].sort((a, b) => {
      const x = g(a), y = g(b);
      const ex = x === "" || x == null, ey = y === "" || y == null;
      if (ex || ey) return ex === ey ? 0 : ex ? 1 : -1;
      return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: "base" })) * dir;
    });
  }, [rows, key, dir]); // eslint-disable-line react-hooks/exhaustive-deps
  const Th = ({ k, children, className = "", testId }: { k: string; children: React.ReactNode; className?: string; testId?: string }) => {
    const on = key === k;
    const Icon = on ? (dir === 1 ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <th className={className} data-col={k} aria-sort={on ? (dir === 1 ? "ascending" : "descending") : "none"}>
        <button type="button" className={`inline-flex items-center gap-1 whitespace-nowrap uppercase hover:text-[#1B365D] ${on ? "text-[#1B365D]" : ""}`}
          onClick={() => { if (on) setDir(dir === 1 ? -1 : 1); else { setKey(k); setDir(1); } }} data-testid={testId || `sort-${k}`}>
          {children}<Icon className={`h-3 w-3 ${on ? "" : "opacity-40"}`} />
        </button>
      </th>
    );
  };
  return { sorted, Th };
}
